import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import {
  Shield,
  Phone,
  Mail,
  MessageCircle,
  ChevronRight,
  Home,
  CheckCircle2,
  Clock,
  Users,
  Award,
  MapPin,
} from "lucide-react";

const ServicePageTemplate = ({
  serviceName,
  heroImage,
  heroTagline,
  heroDescription,
  overviewTitle,
  overviewText,
  useCases,
  safetyFeatures,
  galleryImages,
  faqs,
  icon: IconComponent,
}) => {
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      `Hi, I'm interested in ${serviceName} services from HighKraft Aviation`
    );
    window.open(`https://wa.me/919148010687?text=${message}`, "_blank");
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <nav className="container mx-auto px-4 py-3 md:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 md:gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_dda025fc-cd40-4797-8c5d-d84f34dad8eb/artifacts/i3r7o5v7_HighKraft.jpeg"
              alt="HighKraft Aviation Logo"
              className="w-14 h-14 md:w-20 md:h-20 object-contain"
            />
            <div className="flex flex-col">
              <h1 className="text-lg md:text-2xl lg:text-3xl font-bold text-aviation-blue tracking-tight leading-none">
                HighKraft Aviation
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <div className="h-0.5 w-6 md:w-8 bg-aviation-gold"></div>
                <span className="text-xs font-medium text-aviation-gold uppercase tracking-wider">
                  Premium Experience
                </span>
              </div>
            </div>
          </Link>

          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="hidden md:flex items-center gap-2 text-aviation-blue hover:text-aviation-gold transition-colors"
            >
              <Home className="w-4 h-4" />
              Home
            </Link>
            <Button
              onClick={handleWhatsAppClick}
              className="bg-aviation-gold hover:bg-aviation-gold-dark text-white"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">WhatsApp</span>
            </Button>
          </div>
        </nav>
      </header>

      {/* Breadcrumb */}
      <div className="pt-24 md:pt-28 bg-gray-50">
        <div className="container mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm">
            <Link
              to="/"
              className="text-aviation-blue/70 hover:text-aviation-blue flex items-center gap-1"
            >
              <Home className="w-4 h-4" />
              Home
            </Link>
            <ChevronRight className="w-4 h-4 text-aviation-blue/50" />
            <span className="text-aviation-blue/70">Services</span>
            <ChevronRight className="w-4 h-4 text-aviation-blue/50" />
            <span className="text-aviation-blue font-medium">
              {serviceName}
            </span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative">
        <div className="relative h-[500px] md:h-[600px] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${heroImage})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-aviation-blue/85 to-aviation-blue/75"></div>
          </div>

          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="max-w-3xl text-white">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                {IconComponent && (
                  <IconComponent className="w-5 h-5 text-aviation-gold" />
                )}
                <span className="text-sm font-medium">{heroTagline}</span>
              </div>

              <h1
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
                style={{ textShadow: "2px 2px 8px rgba(0,0,0,0.5)" }}
              >
                {serviceName}
              </h1>
              <p
                className="text-lg md:text-xl mb-8 text-white/90"
                style={{ textShadow: "1px 1px 4px rgba(0,0,0,0.5)" }}
              >
                {heroDescription}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => scrollToSection("enquiry")}
                  size="lg"
                  className="bg-aviation-gold hover:bg-aviation-gold-dark text-white text-lg px-8 py-6 shadow-lg"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Request Quote
                </Button>
                <Button
                  onClick={handleWhatsAppClick}
                  size="lg"
                  variant="outline"
                  className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-aviation-blue text-lg px-8 py-6 shadow-lg"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp Us
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-aviation-blue mb-6">
              {overviewTitle}
            </h2>
            <div className="prose prose-lg max-w-none text-aviation-blue/80">
              {overviewText.split("\n\n").map((paragraph, index) => (
                <p key={index} className="mb-4 leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-aviation-blue mb-4">
              When to Choose {serviceName}
            </h2>
            <p className="text-lg text-aviation-blue/70 max-w-2xl mx-auto">
              Discover the perfect scenarios where our service excels
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {useCases.map((useCase, index) => (
              <Card
                key={index}
                className="hover:shadow-xl transition-all hover:-translate-y-1 border-l-4 border-aviation-gold"
              >
                <CardHeader>
                  <div className="w-12 h-12 bg-aviation-gold/10 rounded-full flex items-center justify-center mb-4 border-2 border-aviation-gold">
                    <CheckCircle2 className="w-6 h-6 text-aviation-gold" />
                  </div>
                  <CardTitle className="text-aviation-blue text-xl">
                    {useCase.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-aviation-blue/70">{useCase.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Safety & Standards */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-aviation-gold/10 border border-aviation-gold px-4 py-2 rounded-full mb-4">
              <Shield className="w-5 h-5 text-aviation-gold" />
              <span className="text-sm font-semibold text-aviation-gold">
                Safety First
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-aviation-blue mb-4">
              Our Safety Standards
            </h2>
            <p className="text-lg text-aviation-blue/70 max-w-2xl mx-auto">
              Your safety is our top priority. We maintain the highest standards
              in the industry.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {safetyFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow border-l-4 border-aviation-gold"
              >
                <CheckCircle2 className="w-8 h-8 text-aviation-gold mb-4" />
                <h3 className="text-xl font-semibold text-aviation-blue mb-2">
                  {feature.title}
                </h3>
                <p className="text-aviation-blue/70">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-aviation-blue mb-4">
              Gallery
            </h2>
            <p className="text-lg text-aviation-blue/70">
              Glimpses of our {serviceName.toLowerCase()} services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {galleryImages.map((image, index) => (
              <div
                key={index}
                className="relative overflow-hidden rounded-xl group cursor-pointer h-64"
              >
                <img
                  src={image}
                  alt={`${serviceName} Gallery ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-aviation-blue mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-aviation-blue/70">
                Common questions about our {serviceName.toLowerCase()} services
              </p>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 px-6"
                >
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-semibold text-aviation-blue">
                      {faq.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-aviation-blue/70">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA / Enquiry Section */}
      <section id="enquiry" className="py-16 md:py-20 bg-aviation-blue">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Book {serviceName}?
            </h2>
            <p className="text-lg text-white/90 mb-8">
              Get in touch with our team for customized quotes and availability.
              We're here to make your experience exceptional.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button
                onClick={handleWhatsAppClick}
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white text-lg px-8 py-6"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp: 9148010687
              </Button>
              <Button
                onClick={() => (window.location.href = "tel:9148010687")}
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-aviation-blue text-lg px-8 py-6"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call Now
              </Button>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-white/80">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-aviation-gold" />
                <span>Jakkur Aerodrome, Bangalore</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-aviation-gold" />
                <span>Available 24/7</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-aviation-blue-dark text-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/80">
              © 2025 HighKraft Aviation Services Pvt Limited. All rights
              reserved.
            </p>
            <div className="flex gap-4">
              <Link
                to="/"
                className="text-sm text-white/80 hover:text-white transition-colors"
              >
                Home
              </Link>
              <Link
                to="/#contact"
                className="text-sm text-white/80 hover:text-white transition-colors"
              >
                Contact
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ServicePageTemplate;
